import { useEffect, useState } from 'react';
import { landingView, canSwitchSection, canOpenSection, SECTION_LABELS } from './sections.js';
import OrderingSection from './OrderingSection.jsx';
import { SESSION_ENDED_EVENT, noteSessionEnded, endedNotice } from './sessionEnded.js';
import SignOutEverywhere from './SignOutEverywhere.jsx';

// The client portal shell: one entry point, and everything common to whichever
// section a client is in. Sign-in, the header, dark mode and switching sections
// live here; each section worries only about itself.
//
// What a person sees comes from the sections they hold, not the URL they were
// sent. One section goes straight in, several offer a choice, none gets an
// explanation rather than an empty screen.

async function clientFetch(path, { method = 'GET', body } = {}) {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    noteSessionEnded(res.status, data);
    const err = new Error(data?.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// Same key the staff app uses, so the preference follows the browser.
function usePersistedState(key, init) {
  const [state, setState] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : init; } catch { return init; }
  });
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(state)); } catch { /* unavailable */ }
  }, [state]);
  return [state, setState];
}

function ErrorNote({ children }) {
  if (!children) return null;
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-[12px] mb-4">{children}</div>
  );
}

function NavyShell({ children }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 font-sans" style={{ background: 'var(--navy)' }}>
      <img src="/av-logo-light.svg" alt="Alton Valley" width="48" height="48" className="mb-3" style={{ filter: 'brightness(0) invert(1)' }} />
      <h1 className="text-gold-light text-lg font-extrabold tracking-wide mb-1">ALTON VALLEY</h1>
      <p className="text-white/40 text-[11px] mb-8">Client Portal</p>
      {children}
    </div>
  );
}

// Why the person is looking at the sign-in screen when they did not sign out:
// their session ran out. Shown where sign-in errors appear, and cleared as
// soon as they try again.
function SignedOutNote({ children }) {
  if (!children) return null;
  return (
    <div role="status" className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2 text-[12px] mb-4">{children}</div>
  );
}

function SignInScreen({ onSignedIn, notice = '' }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [shownNotice, setShownNotice] = useState(notice);
  useEffect(() => { setShownNotice(notice); }, [notice]);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setShownNotice(''); setBusy(true);
    try {
      const res = await clientFetch('client/login', { method: 'POST', body: { email: form.email, password: form.password } });
      onSignedIn({ mustChangePassword: !!res.must_change_password, currentPassword: form.password });
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  return (
    <NavyShell>
      <form onSubmit={submit} className="bg-white rounded-xl p-7 w-full max-w-[340px] shadow-2xl">
        <h2 className="text-sm font-bold text-navy mb-5">Sign in</h2>
        <SignedOutNote>{shownNotice}</SignedOutNote>
        <ErrorNote>{error}</ErrorNote>
        <label className="block text-[11px] font-semibold text-gray-700 mb-1">Email</label>
        <input type="email" autoComplete="email" required value={form.email}
          onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="form-input w-full mb-3.5" />
        <label className="block text-[11px] font-semibold text-gray-700 mb-1">Password</label>
        <input type="password" autoComplete="current-password" required value={form.password}
          onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} className="form-input w-full mb-5" />
        <button type="submit" disabled={busy} className="btn-primary w-full rounded-md py-2.5 text-[13px] font-bold disabled:opacity-60">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <p className="text-center text-[9px] text-gray-400 mt-4">
          Your access details were provided in your welcome email.
        </p>
      </form>
    </NavyShell>
  );
}

function ChangePasswordScreen({ currentPassword, onDone }) {
  const [form, setForm] = useState({ next: '', confirm: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 10) { setError('Choose a password of at least 10 characters.'); return; }
    if (form.next !== form.confirm) { setError('The two passwords do not match.'); return; }
    setError(''); setBusy(true);
    try {
      await clientFetch('client/change-password', { method: 'POST', body: { current_password: currentPassword, new_password: form.next } });
      onDone();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <NavyShell>
      <form onSubmit={submit} className="bg-white rounded-xl p-7 w-full max-w-[340px] shadow-2xl">
        <h2 className="text-sm font-bold text-navy mb-2">Choose your own password</h2>
        <p className="text-[10px] text-gray-500 mb-4">This is your first sign-in, so pick a password only you know.</p>
        <ErrorNote>{error}</ErrorNote>
        <label className="block text-[11px] font-semibold text-gray-700 mb-1">New password</label>
        <input type="password" autoComplete="new-password" required value={form.next}
          onChange={(e) => setForm(f => ({ ...f, next: e.target.value }))} className="form-input w-full mb-3.5" />
        <label className="block text-[11px] font-semibold text-gray-700 mb-1">Confirm it</label>
        <input type="password" autoComplete="new-password" required value={form.confirm}
          onChange={(e) => setForm(f => ({ ...f, confirm: e.target.value }))} className="form-input w-full mb-5" />
        <button type="submit" disabled={busy} className="btn-primary w-full rounded-md py-2.5 text-[13px] font-bold disabled:opacity-60">
          {busy ? 'Saving…' : 'Save and continue'}
        </button>
      </form>
    </NavyShell>
  );
}

function NothingEnabledScreen({ onSignOut }) {
  return (
    <NavyShell>
      <div className="bg-white rounded-xl p-7 w-full max-w-[400px] shadow-2xl text-center">
        <h2 className="text-sm font-bold text-navy mb-2">Nothing is switched on for you yet</h2>
        <p className="text-[11px] text-gray-500 mb-5 leading-relaxed">
          Your sign-in works, but none of the portal's sections have been enabled
          for your account. Contact Alton Valley and we will switch on what you need.
        </p>
        <button onClick={onSignOut} className="btn-secondary border rounded-md px-5 py-2 text-[11px] font-bold">Sign out</button>
      </div>
    </NavyShell>
  );
}

function Chooser({ contact, features, onPick }) {
  return (
    <div className="max-w-2xl mx-auto py-10">
      <h1 className="text-2xl font-extrabold text-navy mb-1">
        Welcome back, <span className="text-gold">{contact.first_name}</span>
      </h1>
      <p className="text-xs text-gray-500 mb-7">What would you like to do?</p>
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
        {features.map(f => (
          <button key={f.key} onClick={() => onPick(f.key)}
            className="bg-white border border-gray-200 hover:border-gold rounded-xl p-5 text-left">
            <div className="text-[13px] font-bold text-navy mb-1">{f.label || SECTION_LABELS[f.key] || f.key}</div>
            <div className="text-[11px] text-gray-500 leading-relaxed">{f.blurb}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Header({ contact, features, section, onSwitch, darkMode, setDarkMode, onSignOut, onSignedOutEverywhere }) {
  return (
    <div className="px-4" style={{ background: 'var(--navy)' }}>
      <div className="max-w-5xl mx-auto flex items-center justify-between h-14">
        <div className="flex items-center gap-2.5">
          <img src="/av-logo-light.svg" alt="" width="26" height="26" style={{ filter: 'brightness(0) invert(1)' }} />
          <div>
            <div className="text-gold-light text-xs font-extrabold tracking-wide">ALTON VALLEY</div>
            <div className="text-white/30 text-[8px]">
              {section ? (SECTION_LABELS[section] || 'Client Portal') : 'Client Portal'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          {canSwitchSection(features) && section && (
            <button onClick={() => onSwitch(null)}
              className="bg-white/10 border border-white/10 rounded-md px-2.5 py-1 text-white/60 text-[9px] font-semibold hover:text-white">
              Switch section
            </button>
          )}
          <div className="text-right hidden sm:block">
            <div className="text-white text-xs font-semibold">{contact.first_name} {contact.last_name}</div>
            <div className="text-white/30 text-[8px]">{contact.client_name || ''}</div>
          </div>
          <button onClick={() => setDarkMode(d => !d)} aria-label="Toggle dark mode"
            className="bg-white/10 border border-white/10 rounded-md px-2 py-1 text-white/60 text-[11px] hover:text-white">
            {darkMode ? '☀' : '☾'}
          </button>
          <SignOutEverywhere endpoint="client/logout-all" onSignedOut={onSignedOutEverywhere}
            className="bg-white/10 border border-white/10 rounded-md px-2.5 py-1 text-white/50 text-[9px] font-semibold hover:text-white/80" />
          <button onClick={onSignOut}
            className="bg-white/10 border border-white/10 rounded-md px-2.5 py-1 text-white/50 text-[9px] font-semibold hover:text-white/80">
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ClientPortalShell({ TimesheetApprovals }) {
  // auth: 'checking' | 'signed-out' | 'change-password' | 'in'
  const [auth, setAuth] = useState({ state: 'checking' });
  const [contact, setContact] = useState(null);
  const [features, setFeatures] = useState([]);
  const [section, setSection] = useState(null);
  const [darkMode, setDarkMode] = usePersistedState('av_dark_mode', false);

  useEffect(() => { document.documentElement.classList.toggle('dark', darkMode); }, [darkMode]);

  const load = async () => {
    try {
      const me = await clientFetch('client/me');
      if (me?.authenticated === false) { setAuth({ state: 'signed-out', notice: endedNotice(me) }); return; }
      setContact(me);
      const held = me.features || [];
      setFeatures(held);
      const landing = landingView(held);
      setSection(landing.view === 'section' ? landing.section : null);
      setAuth({ state: 'in' });
    } catch {
      setAuth({ state: 'signed-out' });
    }
  };

  useEffect(() => { load(); }, []);

  // A request anywhere in the portal found the session had run out.
  useEffect(() => {
    const ended = (e) => {
      setContact(null); setFeatures([]); setSection(null);
      setAuth({ state: 'signed-out', notice: e.detail || '' });
    };
    window.addEventListener(SESSION_ENDED_EVENT, ended);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, ended);
  }, []);

  const signOut = async () => {
    await clientFetch('client/logout', { method: 'POST', body: {} }).catch(() => {});
    setContact(null); setFeatures([]); setSection(null);
    setAuth({ state: 'signed-out' });
  };

  // Every session ended, this one included: back to sign-in, saying so.
  const signedOutEverywhere = (notice) => {
    setContact(null); setFeatures([]); setSection(null);
    setAuth({ state: 'signed-out', notice });
  };

  if (auth.state === 'checking') {
    return <NavyShell><div className="text-white/50 text-[11px]">Signing you in…</div></NavyShell>;
  }
  if (auth.state === 'signed-out') {
    return <SignInScreen notice={auth.notice} onSignedIn={({ mustChangePassword, currentPassword }) => {
      if (mustChangePassword) setAuth({ state: 'change-password', currentPassword });
      else load();
    }} />;
  }
  if (auth.state === 'change-password') {
    return <ChangePasswordScreen currentPassword={auth.currentPassword} onDone={load} />;
  }

  const landing = landingView(features);
  if (landing.view === 'none') return <NothingEnabledScreen onSignOut={signOut} />;

  // Guard the section as well as the chooser: holding a section is what makes
  // it reachable, so a stale choice cannot open something no longer granted.
  const open = section && canOpenSection(features, section) ? section : null;

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Header contact={contact} features={features} section={open} onSwitch={setSection}
        darkMode={darkMode} setDarkMode={setDarkMode} onSignOut={signOut} onSignedOutEverywhere={signedOutEverywhere} />
      <div className="max-w-5xl mx-auto px-4 py-6">
        {!open && <Chooser contact={contact} features={features} onPick={setSection} />}
        {open === 'ordering' && <OrderingSection />}
        {open === 'timesheet-approvals' && TimesheetApprovals && (
          <TimesheetApprovals contact={contact} onLogout={signOut} />
        )}
      </div>
    </div>
  );
}
